/** Macronutrient allocation follows protein-first sports-nutrition ranges and DRI fiber guidance. */
import {
  ALGO_VERSIONS,
  FAT_ABSOLUTE_MIN_G,
  FAT_MIN_G_PER_KG,
  FAT_MIN_KCAL_FRACTION,
  FIBER_G_PER_1000_KCAL,
  FIBER_MAX_G,
  FIBER_MIN_G,
  KCAL_PER_G_CARBS,
  KCAL_PER_G_FAT,
  KCAL_PER_G_PROTEIN,
  PROTEIN_MAX_G,
  PROTEIN_MAX_KCAL_FRACTION,
  PROTEIN_MIN_G,
  PROTEIN_PER_KG,
} from "./constants";
import { calculateBmi } from "./metabolism";
import {
  ENGINE_VERSION,
  type EngineMeta,
  type EngineProfile,
  type EngineResult,
  type MacroTargetsValue,
} from "./types";

const FALLBACK_KCAL = 2000;
const FALLBACK_WEIGHT_KG = 70;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function proteinPerKg(goalType: string | undefined): number {
  switch (goalType) {
    case "lose":
    case "muscle":
    case "gain":
    case "maintain":
    case "healthier":
      return PROTEIN_PER_KG[goalType];
    default:
      return 1.4;
  }
}

function selectReferenceWeight(profile: EngineProfile, now: number): {
  readonly weightKg: number;
  readonly source: "goal" | "current" | "fallback";
} {
  const bmi = calculateBmi(profile, now)?.value.bmi;
  if (
    profile.goalType === "lose" &&
    bmi !== undefined &&
    bmi >= 25 &&
    profile.targetWeightKg !== undefined
  ) {
    return { weightKg: profile.targetWeightKg, source: "goal" };
  }
  if (profile.weightKg !== undefined) return { weightKg: profile.weightKg, source: "current" };
  return { weightKg: FALLBACK_WEIGHT_KG, source: "fallback" };
}

function referenceLabel(source: "goal" | "current" | "fallback"): string {
  if (source === "goal") return "goal weight";
  if (source === "current") return "current weight";
  return "reference weight";
}

function allocateRemainder(
  dietType: string | undefined,
  targetKcal: number,
  remainingKcal: number,
  minFatG: number,
): { readonly carbsG: number; readonly fatG: number; readonly rationale: string } {
  if (dietType === "keto") {
    const carbsG = Math.min(50, Math.max(0, Math.floor(remainingKcal / KCAL_PER_G_CARBS)));
    const fatG = Math.max(
      minFatG,
      Math.round((remainingKcal - carbsG * KCAL_PER_G_CARBS) / KCAL_PER_G_FAT),
    );
    return { carbsG, fatG, rationale: "Keto preference caps carbohydrates at 50 g and assigns the rest to fat." };
  }
  if (dietType === "low-carb") {
    const desiredCarbsG = Math.round((targetKcal * 0.25) / KCAL_PER_G_CARBS);
    const affordableCarbsG = Math.max(
      0,
      Math.floor((remainingKcal - minFatG * KCAL_PER_G_FAT) / KCAL_PER_G_CARBS),
    );
    const carbsG = Math.min(desiredCarbsG, affordableCarbsG);
    const fatG = Math.max(
      minFatG,
      Math.round((remainingKcal - carbsG * KCAL_PER_G_CARBS) / KCAL_PER_G_FAT),
    );
    return { carbsG, fatG, rationale: "Low-carb preference assigns about 25% of total energy to carbohydrates." };
  }
  if (dietType === "high-protein") {
    const extraKcal = Math.max(0, remainingKcal - minFatG * KCAL_PER_G_FAT);
    return {
      carbsG: Math.round((extraKcal * 0.5) / KCAL_PER_G_CARBS),
      fatG: minFatG + Math.round((extraKcal * 0.5) / KCAL_PER_G_FAT),
      rationale: "High-protein preference splits energy left after protein and minimum fat evenly between carbohydrates and extra fat.",
    };
  }

  const extraKcal = Math.max(0, remainingKcal - minFatG * KCAL_PER_G_FAT);
  return {
    carbsG: Math.round((extraKcal * 0.55) / KCAL_PER_G_CARBS),
    fatG: minFatG + Math.round((extraKcal * 0.45) / KCAL_PER_G_FAT),
    rationale: "Balanced allocation gives about 55% of energy left after minimum fat to carbohydrates and 45% to extra fat.",
  };
}

function createMeta(
  now: number,
  inputs: EngineMeta["inputs"],
  usedFallbackKcal: boolean,
): EngineMeta {
  return {
    kind: "macroTargets",
    engineVersion: ENGINE_VERSION,
    algorithmVersion: ALGO_VERSIONS.macroTargets,
    computedAt: now,
    estimateKind: "initial",
    confidence: "moderate",
    explanation: usedFallbackKcal
      ? "Targets use a 2,000 kcal fallback because a positive calorie target was unavailable; protein and minimum fat are allocated before carbohydrates."
      : "Protein and minimum fat are allocated first, then remaining calories follow the selected diet preference.",
    inputs,
  };
}

export function calculateMacroTargets(
  profile: EngineProfile,
  targetKcal: number,
  now = Date.now(),
): EngineResult<MacroTargetsValue> {
  const usedFallbackKcal = targetKcal <= 0;
  const effectiveKcal = usedFallbackKcal ? FALLBACK_KCAL : targetKcal;
  const reference = selectReferenceWeight(profile, now);
  const perKg = proteinPerKg(profile.goalType);
  const proteinCalorieCapG = Math.floor(
    (effectiveKcal * PROTEIN_MAX_KCAL_FRACTION) / KCAL_PER_G_PROTEIN,
  );
  const requestedProteinG = Math.round(reference.weightKg * perKg);
  const minFatG = Math.round(
    Math.max(
      FAT_MIN_G_PER_KG * reference.weightKg,
      (FAT_MIN_KCAL_FRACTION * effectiveKcal) / KCAL_PER_G_FAT,
      FAT_ABSOLUTE_MIN_G,
    ),
  );
  const kcalAfterMinimumFat = Math.max(0, effectiveKcal - minFatG * KCAL_PER_G_FAT);
  const budgetProteinG = Math.floor(kcalAfterMinimumFat / KCAL_PER_G_PROTEIN);
  const standardProteinFloor = effectiveKcal < 800
    ? Math.floor((effectiveKcal * 0.25) / KCAL_PER_G_PROTEIN)
    : PROTEIN_MIN_G;
  const proteinG = Math.max(
    0,
    Math.min(
      clamp(requestedProteinG, standardProteinFloor, PROTEIN_MAX_G),
      proteinCalorieCapG,
      budgetProteinG,
    ),
  );
  const remainingKcal = Math.max(0, effectiveKcal - proteinG * KCAL_PER_G_PROTEIN);
  const allocation = allocateRemainder(profile.dietType, effectiveKcal, remainingKcal, minFatG);
  const fiberG = clamp(
    Math.round((effectiveKcal / 1000) * FIBER_G_PER_1000_KCAL),
    FIBER_MIN_G,
    FIBER_MAX_G,
  );
  const rationale = [
    `Protein set at ${perKg} g/kg of ${referenceLabel(reference.source)} (${roundOne(reference.weightKg)} kg) for ${profile.goalType === "lose" ? "muscle retention during weight loss" : "daily protein coverage"}.`,
    `Minimum fat set at ${minFatG} g before allocating remaining calories.`,
    allocation.rationale,
    `Fiber set from 14 g per 1,000 kcal and clamped to a practical daily range.`,
  ];
  if (usedFallbackKcal) rationale.push("A 2,000 kcal fallback was used because the supplied calorie target was not positive.");
  if (proteinG < requestedProteinG) rationale.push("Protein was reduced to fit the calorie budget while preserving minimum fat.");

  return {
    value: {
      proteinG,
      carbsG: allocation.carbsG,
      fatG: allocation.fatG,
      fiberG,
      proteinReferenceWeightKg: roundOne(reference.weightKg),
      proteinPerKg: perKg,
      minFatG,
      rationale,
    },
    meta: createMeta(now, {
      targetKcal: Math.round(effectiveKcal),
      suppliedTargetKcal: targetKcal,
      goalType: profile.goalType ?? "unknown",
      dietType: profile.dietType ?? "balanced",
      proteinReferenceWeightKg: roundOne(reference.weightKg),
      proteinReferenceSource: reference.source,
    }, usedFallbackKcal),
  };
}
